import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Eye,
  Receipt,
  Calendar,
  Plus,
  Copy,
  Trash2,
  Filter,
  X,
  Download
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { toast } from "sonner";
import { API } from "@/lib/api";

const SERVICE_TYPES = [
  "Ironing",
  "Wash and Fold",
  "Wash and Iron",
  "Dry Cleaning",
  "Tailoring",
  "Other Services",
  "Commerce",
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(amount);

export const VendorInvoiceHistory = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [rmFilter, setRmFilter] = useState("all");
  const [payeeFilter, setPayeeFilter] = useState("all");
  const [staffList, setStaffList] = useState([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      const res = await fetch(`${API}/vendor-invoices/export.csv?${params}`);
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : "vendor_invoices.csv";
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Vendor invoices exported");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export vendor invoices");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetch(`${API}/staff`).then(r => r.json()).then(setStaffList).catch(() => {});
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      const res = await fetch(`${API}/vendor-invoices?${params}`);
      if (!res.ok) throw new Error("Failed to fetch");
      setInvoices(await res.json());
    } catch {
      toast.error("Failed to load vendor invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [search, startDate, endDate]);

  const rms = staffList.filter(s => s.team === "Relations Manager");
  const payees = staffList.filter(s => s.team === "Payee");

  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      if (serviceFilter !== "all" && !(inv.line_items || []).some(item => item.service_type?.toLowerCase() === serviceFilter.toLowerCase())) return false;
      if (rmFilter !== "all" && inv.rm_name !== rmFilter) return false;
      if (payeeFilter !== "all" && inv.payee_name !== payeeFilter) return false;
      return true;
    });
  }, [invoices, serviceFilter, rmFilter, payeeFilter]);

  const totalAmount = filteredInvoices.reduce((sum, inv) => sum + inv.total_payable, 0);

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API}/vendor-invoices/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      setInvoices((prev) => prev.filter((inv) => inv.id !== id));
      toast.success("Invoice deleted");
    } catch {
      toast.error("Failed to delete invoice");
    } finally {
      setConfirmDeleteId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="vendor-invoice-history-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Vendor Invoice History</h1>
          <p className="text-slate-500 mt-1">All vendor invoices and their signature status</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExportCSV}
            disabled={exporting || loading}
            className="gap-2"
            data-testid="export-vendor-csv-btn"
          >
            <Download className="w-4 h-4" />
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
          <Link to="/vendor-invoice">
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-vendor-invoice-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Invoice
            </Button>
          </Link>
        </div>
      </div>

      {/* Summary */}
      {!loading && invoices.length > 0 && (
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Total Invoices</p>
              <p className="text-2xl font-bold font-mono text-slate-900" data-testid="summary-total-count">
                {filteredInvoices.length}{serviceFilter !== "all" && <span className="text-base text-slate-400"> / {invoices.length}</span>}
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Total Paid Out</p>
              <p className="text-2xl font-bold font-mono text-emerald-600" data-testid="summary-total-amount">{formatCurrency(totalAmount)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          {/* Row 1: Search + Date */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by invoice no. or vendor name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-36 text-sm"
                data-testid="start-date-input"
              />
              <span className="text-slate-400 text-sm">–</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-36 text-sm"
                data-testid="end-date-input"
              />
            </div>
          </div>
          {/* Row 2: Service + RM + Payee Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-500 shrink-0">
              <Filter className="w-4 h-4" />
            </div>
            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger className="w-40 text-sm h-9" data-testid="service-filter">
                <SelectValue placeholder="All services" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All services</SelectItem>
                {SERVICE_TYPES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={rmFilter} onValueChange={setRmFilter}>
              <SelectTrigger className="w-40 text-sm h-9" data-testid="rm-filter">
                <SelectValue placeholder="All RMs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All RMs</SelectItem>
                {rms.map((r) => (
                  <SelectItem key={r.id} value={r.full_name}>{r.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={payeeFilter} onValueChange={setPayeeFilter}>
              <SelectTrigger className="w-40 text-sm h-9" data-testid="payee-filter">
                <SelectValue placeholder="All Payees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payees</SelectItem>
                {payees.map((p) => (
                  <SelectItem key={p.id} value={p.full_name}>{p.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(serviceFilter !== "all" || rmFilter !== "all" || payeeFilter !== "all") && (
              <Button variant="ghost" size="sm" className="h-9 text-slate-500 hover:text-slate-700 gap-1"
                onClick={() => { setServiceFilter("all"); setRmFilter("all"); setPayeeFilter("all"); }}>
                <X className="w-3.5 h-3.5" /> Clear
              </Button>
            )}
            {(serviceFilter !== "all" || rmFilter !== "all" || payeeFilter !== "all") && (
              <span className="text-xs text-slate-400">
                {filteredInvoices.length} result{filteredInvoices.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Invoice List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-pulse text-slate-500">Loading invoices...</div>
        </div>
      ) : filteredInvoices.length > 0 ? (
        <div className="space-y-3" data-testid="vendor-invoices-list">
          {filteredInvoices.map((inv) => (
            <Card
              key={inv.id}
              className="border-slate-200 hover:shadow-md transition-shadow"
              data-testid={`vendor-invoice-row-${inv.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-50 shrink-0">
                      <Receipt className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-semibold text-slate-800" data-testid={`inv-number-${inv.id}`}>
                          {inv.invoice_number}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${inv.signature_status === "Signed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                          {inv.signature_status === "Signed" ? "Signed" : "Pending Signature"}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-0.5 font-medium">{inv.vendor_name}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {inv.vendor_service_type} &middot; {format(new Date(inv.invoice_date), "dd MMM yyyy")}
                        {inv.rm_name && <> &middot; RM: {inv.rm_name}</>}
                        {inv.payee_name && <> &middot; Payee: {inv.payee_name}</>}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <p className="font-mono font-bold text-slate-900 text-base" data-testid={`inv-total-${inv.id}`}>
                        {formatCurrency(inv.total_payable)}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">{(inv.line_items || []).length} item{(inv.line_items || []).length !== 1 ? "s" : ""}</p>
                    </div>
                    {inv.signature_status !== "Signed" && (
                      <Button variant="ghost" size="sm" className="gap-1.5 text-amber-600 hover:text-amber-700" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/sign/${inv.signing_token}`); toast.success("Link copied!"); }}>
                        <Copy className="w-3.5 h-3.5" />
                        Copy Link
                      </Button>
                    )}
                    <Link to={`/vendor-invoice/${inv.id}`}>
                      <Button variant="outline" size="sm" className="gap-1.5" data-testid={`view-vendor-invoice-${inv.id}`}>
                        <Eye className="w-3.5 h-3.5" />
                        View
                      </Button>
                    </Link>
                    {confirmDeleteId === inv.id ? (
                      <div className="flex items-center gap-1">
                        <Button variant="destructive" size="sm" onClick={() => handleDelete(inv.id)}>
                          Confirm
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteId(null)}>
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button variant="ghost" size="sm" className="text-slate-400 hover:text-red-500" onClick={() => setConfirmDeleteId(inv.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <Receipt className="w-14 h-14 text-slate-200 mx-auto mb-4" />
            {(serviceFilter !== "all" || rmFilter !== "all" || payeeFilter !== "all") ? (
              <>
                <h3 className="text-base font-medium text-slate-600 mb-2">No invoices match the selected filters</h3>
                <p className="text-slate-400 text-sm mb-6">Try adjusting or clearing the filters</p>
                <Button variant="outline" onClick={() => { setServiceFilter("all"); setRmFilter("all"); setPayeeFilter("all"); }}>
                  <X className="w-4 h-4 mr-2" /> Clear Filters
                </Button>
              </>
            ) : (
              <>
                <h3 className="text-base font-medium text-slate-600 mb-2">No vendor invoices yet</h3>
                <p className="text-slate-400 text-sm mb-6">Create your first invoice to get started</p>
                <Link to="/vendor-invoice">
                  <Button className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Invoice
                  </Button>
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
